import React, { useState } from 'react';
import { ChevronDown, Code, Copy, CheckCheck } from 'lucide-react';
import type { ButtonCustomization } from '../../lib/types';

interface InstallationGuideProps {
  buttonConfig: ButtonCustomization;
}

const InstallationGuide: React.FC<InstallationGuideProps> = ({ buttonConfig }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const scriptCode = `<!-- Add to your <head> section -->
<script crossorigin src="https://unpkg.com/react@18/umd/react.production.min.js"></script>
<script crossorigin src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"></script>
<script type="module" src="https://glittering-mousse-2afca0.netlify.app/assets/index._AC06DE3.js"></script>
<script type="module">
  window.addEventListener('load', function() {
    if (window.AdSpotter) {
      window.AdSpotter.init({
        buttonStyle: ${JSON.stringify(buttonConfig, null, 2)}
      });
    }
  });
</script>`;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Code className="w-5 h-5 text-gray-500" />
          <span className="text-xl font-semibold text-gray-900">
            How It Works
          </span>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-gray-500 transition-transform ${
            isOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-indigo-600">1</span>
              </div>
              <h3 className="font-semibold mb-2">Copy the Code</h3>
              <p className="text-gray-600 text-sm">
                Copy the installation code snippet provided below
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-indigo-600">2</span>
              </div>
              <h3 className="font-semibold mb-2">Add to Your Website</h3>
              <p className="text-gray-600 text-sm">
                Paste the code into your website's HTML head section
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-indigo-600">3</span>
              </div>
              <h3 className="font-semibold mb-2">Start Receiving Offers</h3>
              <p className="text-gray-600 text-sm">
                The widget will appear on your site, ready to receive ad space inquiries
              </p>
            </div>
          </div>

          <div className="relative">
            <pre className="bg-gray-50 text-gray-800 p-4 rounded-lg text-sm overflow-x-auto border border-gray-200">
              {scriptCode}
            </pre>
            <button
              onClick={() => handleCopy(scriptCode, 'script')}
              className="absolute top-2 right-2 px-3 py-1 bg-gray-800 text-white rounded hover:bg-gray-700 text-sm flex items-center gap-1"
            >
              {copiedId === 'script' ? (
                <>
                  <CheckCheck className="w-4 h-4" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy
                </>
              )}
            </button>
          </div>

          <p className="mt-4 text-sm text-gray-600">
            Add this code to your website to start receiving ad space inquiries immediately.
          </p>
        </div>
      )}
    </div>
  );
};

export default InstallationGuide;